import React, { useRef, useState, useCallback, createContext, useContext } from 'react';
import type { MessageDescriptor } from '@lingui/core';
import { useLingui } from '@lingui/react';

interface ToastAction {
  label: MessageDescriptor;
  onClick: () => void;
}

interface Toast {
  id: number;
  message: MessageDescriptor;
  action?: ToastAction;
}

interface ToastContextValue {
  showToast: (message: MessageDescriptor, action?: ToastAction) => void;
}

const ToastContext = createContext<ToastContextValue>({ showToast: () => {} });

export function useToast() {
  return useContext(ToastContext);
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const { i18n } = useLingui();
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(0);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const showToast = useCallback((message: MessageDescriptor, action?: ToastAction) => {
    const id = nextId.current++;
    setToasts((prev) => [...prev, { id, message, action }]);
    setTimeout(() => dismiss(id), 4000);
  }, [dismiss]);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      {toasts.length > 0 && (
        <div className="toast-container">
          {toasts.map((t) => (
            <div key={t.id} className="toast-item">
              {i18n._(t.message)}
              {t.action && (
                <button
                  className="toast-action"
                  onClick={() => {
                    dismiss(t.id);
                    t.action!.onClick();
                  }}
                >
                  {i18n._(t.action.label)}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </ToastContext.Provider>
  );
}
